import { type JSX, lazy, startTransition } from 'react';
import { Link, matchPath } from 'react-router';

import { pageurl } from '../constants/pageurl';

export type PreloadableComponent<T extends React.ComponentType<any>> = React.LazyExoticComponent<T> & {
	preload?: () => Promise<void>;
};

export function ReactLazyPreload<T extends React.ComponentType<any>>(
	importStatement: () => Promise<{ default: T }>
): PreloadableComponent<T> {
	const Component = lazy(importStatement) as PreloadableComponent<T>;

	Component.preload = async () =>
		await new Promise<void>((resolve) => {
			startTransition(() => {
				importStatement().then(() => resolve());
			});
		});

	return Component;
}

interface IRouteComponent {
	path: string;
	Component: PreloadableComponent<() => JSX.Element>;
}

export const findComponentForRoute = (
	path: string,
	routes: IRouteComponent[]
): PreloadableComponent<() => JSX.Element> | null => {
	const matchingRoute = routes.find((route) => matchPath(route.path, path));

	return matchingRoute ? matchingRoute.Component : null;
};

export const routeComponent: IRouteComponent[] = [];

export const preloadRouteComponent = (path: string) => {
	const component = findComponentForRoute(path, routeComponent);

	if (component?.preload) {
		component.preload();
	}
};

export const PreloadLink = ({ to, ...rest }: { to: any }) => (
	<Link
		to={to}
		onMouseEnter={() => preloadRouteComponent(to as string)}
		{...rest}
	/>
);
